import React, { useEffect, useState, useMemo } from 'react';
import { landlordAPI, getUploadUrl } from '../../lib/api';
import { formatCurrency, formatDate, getStatusColor, getPaymentMethodColor, getInitials, downloadBlob } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { CreditCard, CheckCircle, XCircle, Eye, Loader2, Search, Pencil, Filter, X, Download, FileText, Trash2, Users, UserCheck, UserX } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { useRoleScope } from '../../hooks/useRoleScope';

function PaymentStatusTab({ payments, tenants, leases, statusPeriod, setStatusPeriod }) {
  const activeLeases = useMemo(() => leases.filter(l => l.status === 'active'), [leases]);
  
  const { paid, unpaid } = useMemo(() => {
    const verifiedForPeriod = (payments || []).filter(
      p => p.status === 'verified' && p.period_month === statusPeriod
    );
    const paidTenantIds = new Set(verifiedForPeriod.map(p => p.tenant_id));

    const paidList = [];
    const unpaidList = [];

    activeLeases.forEach(lease => {
      const tid = lease.tenant_id;
      const tenant = tenants.find(t => t.user_id === tid) || {};
      const entry = {
        tenant_id: tid,
        name: tenant.name || lease.tenant?.name || 'Unknown',
        email: tenant.email || lease.tenant?.email || '',
        unit: lease.unit?.unit_number || lease.unit_id || '',
        property: lease.property?.name || lease.property_id || '',
        rent: lease.monthly_rent || 0,
      };

      if (paidTenantIds.has(tid)) {
        const payment = verifiedForPeriod.find(p => p.tenant_id === tid);
        entry.amount_paid = payment?.amount || 0;
        entry.payment_date = payment?.payment_date || '';
        entry.payment_method = payment?.payment_method || '';
        paidList.push(entry);
      } else {
        unpaidList.push(entry);
      }
    });

    return { paid: paidList, unpaid: unpaidList };
  }, [payments, activeLeases, tenants, statusPeriod]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Payment Status by Tenant
              </CardTitle>
              <CardDescription>Who paid and who hasn't for a given period</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">Period:</Label>
              <Input
                type="month"
                value={statusPeriod}
                onChange={(e) => setStatusPeriod(e.target.value)}
                className="w-44"
                data-testid="payment-status-period"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-muted text-center">
              <p className="text-2xl font-bold">{activeLeases.length}</p>
              <p className="text-xs text-muted-foreground">Active Leases</p>
            </div>
            <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 text-center">
              <p className="text-2xl font-bold text-emerald-600">{paid.length}</p>
              <p className="text-xs text-muted-foreground">Paid</p>
            </div>
            <div className="p-4 rounded-lg bg-red-50 dark:bg-red-950/30 text-center">
              <p className="text-2xl font-bold text-red-600">{unpaid.length}</p>
              <p className="text-xs text-muted-foreground">Not Yet Paid</p>
            </div>
          </div>

          {activeLeases.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No active leases found.</p>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {/* Paid Column */}
              <div className="space-y-2">
                <h3 className="flex items-center gap-2 font-semibold text-emerald-700 dark:text-emerald-400">
                  <UserCheck className="w-4 h-4" /> Paid ({paid.length})
                </h3>
                {paid.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-3 bg-muted rounded-lg">No tenants have paid for this period yet.</p>
                ) : paid.map((t, i) => (
                  <div key={t.tenant_id || t.email || `paid-${i}`} className="flex items-center justify-between p-3 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20">
                    <div>
                      <p className="font-medium text-sm">{t.name}</p>
                      <p className="text-xs text-muted-foreground">{t.unit} &middot; {t.property}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-sm text-emerald-600">{formatCurrency(t.amount_paid)}</p>
                      <p className="text-xs text-muted-foreground capitalize">{t.payment_method}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Unpaid Column */}
              <div className="space-y-2">
                <h3 className="flex items-center gap-2 font-semibold text-red-700 dark:text-red-400">
                  <UserX className="w-4 h-4" /> Not Yet Paid ({unpaid.length})
                </h3>
                {unpaid.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-3 bg-muted rounded-lg">All tenants have paid for this period!</p>
                ) : unpaid.map((t, i) => (
                  <div key={t.tenant_id || t.email || `unpaid-${i}`} className="flex items-center justify-between p-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-950/20">
                    <div>
                      <p className="font-medium text-sm">{t.name}</p>
                      <p className="text-xs text-muted-foreground">{t.unit} &middot; {t.property}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-sm text-red-600">Due: {formatCurrency(t.rent)}</p>
                      <p className="text-xs text-muted-foreground">{t.email}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function LandlordPayments() {
  const { t } = useLanguage();
  const { isManager, can } = useRoleScope();
  const [payments, setPayments] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [statusPeriod, setStatusPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  // Filter states
  const [filterName, setFilterName] = useState('');
  const [filterPaymentType, setFilterPaymentType] = useState('all');
  const [filterPaymentMethod, setFilterPaymentMethod] = useState('all');
  const [filterPeriodMonth, setFilterPeriodMonth] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Edit payment states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [editForm, setEditForm] = useState({
    amount: '', payment_date: '', payment_type: '', payment_method: '',
    period_month: '', reference_number: '', notes: ''
  });

  useEffect(() => { loadPayments(); loadTenantsAndLeases(); }, []);

  const loadPayments = async () => {
    try {
      const response = await landlordAPI.getPayments();
      setPayments(response.data);
    } catch (error) {
      console.error('Error loading payments:', error);
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const loadTenantsAndLeases = async () => {
    try {
      const [tenantsRes, leasesRes] = await Promise.all([
        landlordAPI.getTenants(),
        landlordAPI.getLeases()
      ]);
      setTenants(tenantsRes.data || []);
      setLeases(leasesRes.data || []);
    } catch (error) {
      console.error('Error loading tenants/leases:', error);
    }
  };

  // Filter logic
  const filteredPayments = useMemo(() => {
    let result = activeTab === 'all' ? payments : payments.filter(p => p.status === activeTab);
    
    if (filterName.trim()) {
      const q = filterName.toLowerCase();
      result = result.filter(p => (p.tenant?.name || '').toLowerCase().includes(q));
    }
    if (filterPaymentType !== 'all') {
      result = result.filter(p => p.payment_type === filterPaymentType);
    }
    if (filterPaymentMethod !== 'all') {
      result = result.filter(p => p.payment_method === filterPaymentMethod);
    }
    if (filterPeriodMonth) {
      result = result.filter(p => p.period_month === filterPeriodMonth);
    }
    if (filterDate) {
      result = result.filter(p => (p.payment_date || '').startsWith(filterDate));
    }
    return result;
  }, [payments, activeTab, filterName, filterPaymentType, filterPaymentMethod, filterPeriodMonth, filterDate]);

  const hasActiveFilters = filterName || filterPaymentType !== 'all' || filterPaymentMethod !== 'all' || filterPeriodMonth || filterDate;

  const clearFilters = () => {
    setFilterName('');
    setFilterPaymentType('all');
    setFilterPaymentMethod('all');
    setFilterPeriodMonth('');
    setFilterDate('');
  };

  // Tab counts (unfiltered)
  const tabCounts = useMemo(() => ({
    pending: payments.filter(p => p.status === 'pending').length,
    verified: payments.filter(p => p.status === 'verified').length,
    rejected: payments.filter(p => p.status === 'rejected').length,
    all: payments.length
  }), [payments]);

  const handleVerify = async (paymentId) => {
    setProcessing(true);
    try {
      await landlordAPI.verifyPayment(paymentId);
      toast.success(t('payments.verifySuccess'));
      setSelectedPayment(null);
      loadPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.somethingWentWrong'));
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (paymentId) => {
    const reason = window.prompt(t('payments.enterRejectionReason'));
    if (reason === null) return;
    setProcessing(true);
    try {
      await landlordAPI.rejectPayment(paymentId, reason);
      toast.success(t('payments.rejectSuccess'));
      setSelectedPayment(null);
      loadPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.somethingWentWrong'));
    } finally {
      setProcessing(false);
    }
  };

  // Edit payment
  const openEditDialog = (payment) => {
    setEditingPayment(payment);
    setEditForm({
      amount: payment.amount || '',
      payment_date: payment.payment_date || '',
      payment_type: payment.payment_type || '',
      payment_method: payment.payment_method || '',
      period_month: payment.period_month || '',
      reference_number: payment.reference_number || '',
      notes: payment.notes || ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingPayment) return;
    setSavingEdit(true);
    try {
      const payload = {};
      if (editForm.amount) payload.amount = parseFloat(editForm.amount);
      if (editForm.payment_date) payload.payment_date = editForm.payment_date;
      if (editForm.payment_type) payload.payment_type = editForm.payment_type;
      if (editForm.payment_method) payload.payment_method = editForm.payment_method;
      if (editForm.period_month !== undefined) payload.period_month = editForm.period_month;
      if (editForm.reference_number !== undefined) payload.reference_number = editForm.reference_number;
      if (editForm.notes !== undefined) payload.notes = editForm.notes;

      await landlordAPI.editPayment(editingPayment.payment_id, payload);
      toast.success('Payment updated successfully');
      setEditDialogOpen(false);
      setEditingPayment(null);
      loadPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update payment');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDownloadReceipt = async (paymentId, receiptNumber) => {
    setDownloading(paymentId);
    try {
      const response = await landlordAPI.downloadReceipt(paymentId);
      downloadBlob(response.data, `receipt_${receiptNumber}_landlord.pdf`);
      toast.success('Receipt downloaded');
    } catch (error) {
      toast.error('Failed to download receipt');
    } finally {
      setDownloading(null);
    }
  };

  const handleDeletePayment = async (payment) => {
    if (!window.confirm(`Delete this ${payment.payment_type} payment of ${formatCurrency(payment.amount)} from ${payment.tenant?.name || 'Unknown'}? This cannot be undone.`)) return;
    try {
      await landlordAPI.deletePayment(payment.payment_id);
      toast.success('Payment deleted');
      setSelectedPayment(null);
      loadPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete payment');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="landlord-payments-page">
      <div>
        <h1 className="font-heading text-2xl lg:text-3xl font-bold">{t('payments.title')}</h1>
        <p className="text-muted-foreground mt-1">{t('payments.reviewAndVerify')}</p>
      </div>

      {/* Filters */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Find by tenant name..."
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              className="pl-10"
              data-testid="payment-filter-name"
            />
            {filterName && (
              <button onClick={() => setFilterName('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button
            variant={showFilters ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
            {hasActiveFilters && <Badge variant="secondary" className="ml-2 h-5 px-1.5 bg-red-100 text-red-700">{[filterPaymentType !== 'all', filterPaymentMethod !== 'all', filterPeriodMonth, filterDate].filter(Boolean).length}</Badge>}
          </Button>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
              <X className="w-4 h-4 mr-1" /> Clear All
            </Button>
          )}
        </div>

        {showFilters && (
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">{t('payments.paymentType')}</Label>
                  <Select value={filterPaymentType} onValueChange={setFilterPaymentType}>
                    <SelectTrigger data-testid="filter-payment-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="rent">{t('payments.rent')}</SelectItem>
                      <SelectItem value="deposit">{t('payments.deposit')}</SelectItem>
                      <SelectItem value="advance">{t('payments.advance')}</SelectItem>
                      <SelectItem value="utility">{t('payments.utility')}</SelectItem>
                      <SelectItem value="other">{t('payments.other')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">{t('payments.paymentMethod')}</Label>
                  <Select value={filterPaymentMethod} onValueChange={setFilterPaymentMethod}>
                    <SelectTrigger data-testid="filter-payment-method">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Methods</SelectItem>
                      <SelectItem value="cash">{t('payments.cash')}</SelectItem>
                      <SelectItem value="gcash">{t('payments.gcash')}</SelectItem>
                      <SelectItem value="maya">{t('payments.maya')}</SelectItem>
                      <SelectItem value="bank">{t('payments.bank')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">{t('payments.periodMonth')}</Label>
                  <Input
                    type="month"
                    value={filterPeriodMonth}
                    onChange={(e) => setFilterPeriodMonth(e.target.value)}
                    data-testid="filter-period-month"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Payment Date</Label>
                  <Input
                    type="date"
                    value={filterDate}
                    onChange={(e) => setFilterDate(e.target.value)}
                    data-testid="filter-date"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending">
            {t('status.pending')} ({tabCounts.pending})
          </TabsTrigger>
          <TabsTrigger value="verified">
            {t('status.verified')} ({tabCounts.verified})
          </TabsTrigger>
          <TabsTrigger value="rejected">
            {t('status.rejected')} ({tabCounts.rejected})
          </TabsTrigger>
          <TabsTrigger value="all">{t('common.all')} ({tabCounts.all})</TabsTrigger>
          <TabsTrigger value="status" data-testid="payment-status-tab">
            <Users className="w-4 h-4 mr-1" />
            Payment Status
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {filteredPayments.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="font-semibold text-lg mb-2">
                  {hasActiveFilters ? 'No payments match your filters' : `${t('payments.noPayments')} (${activeTab})`}
                </h3>
                <p className="text-muted-foreground">
                  {hasActiveFilters ? 'Try adjusting your filter criteria' : t('payments.paymentsWillAppear')}
                </p>
                {hasActiveFilters && (
                  <Button variant="outline" size="sm" className="mt-4" onClick={clearFilters}>Clear Filters</Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>{t('nav.tenants')}</th>
                        <th>{t('common.amount')}</th>
                        <th>{t('payments.paymentType')}</th>
                        <th>{t('payments.paymentMethod')}</th>
                        <th>Ref #</th>
                        <th>{t('payments.periodMonth')}</th>
                        <th>Payment Date</th>
                        <th>{t('common.status')}</th>
                        <th>{t('common.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPayments.map((payment) => (
                        <tr key={payment.payment_id}>
                          <td>
                            <div className="flex items-center gap-2">
                              <Avatar className="w-8 h-8">
                                <AvatarFallback className="bg-[#6B8E6B] text-white text-xs">
                                  {getInitials(payment.tenant?.name)}
                                </AvatarFallback>
                              </Avatar>
                              <span className="font-medium">{payment.tenant?.name || 'Unknown'}</span>
                            </div>
                          </td>
                          <td className="font-semibold">{formatCurrency(payment.amount)}</td>
                          <td className="capitalize">{payment.payment_type}</td>
                          <td>
                            <Badge className={getPaymentMethodColor(payment.payment_method)}>
                              {payment.payment_method?.toUpperCase()}
                            </Badge>
                          </td>
                          <td className="font-mono text-xs text-muted-foreground">{payment.reference_number || '-'}</td>
                          <td>{payment.period_month || '-'}</td>
                          <td>{payment.payment_date ? formatDate(payment.payment_date) : formatDate(payment.created_at)}</td>
                          <td>
                            <Badge className={getStatusColor(payment.status)}>{payment.status}</Badge>
                          </td>
                          <td>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSelectedPayment(payment)}
                                data-testid={`view-payment-${payment.payment_id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(payment)}
                                data-testid={`edit-payment-${payment.payment_id}`}
                                style={{ display: isManager && !can('verify_payments') ? 'none' : undefined }}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeletePayment(payment)}
                                data-testid={`delete-payment-${payment.payment_id}`}
                                style={{ display: isManager && !can('verify_payments') ? 'none' : undefined }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                              {payment.status === 'verified' && payment.receipt_number && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-emerald-600"
                                  onClick={() => handleDownloadReceipt(payment.payment_id, payment.receipt_number)}
                                  disabled={downloading === payment.payment_id}
                                  data-testid={`download-receipt-${payment.payment_id}`}
                                >
                                  {downloading === payment.payment_id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Download className="w-4 h-4" />
                                  )}
                                </Button>
                              )}
                              {payment.status === 'pending' && (!isManager || can('verify_payments')) && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-emerald-600"
                                    onClick={() => handleVerify(payment.payment_id)}
                                    data-testid={`verify-payment-${payment.payment_id}`}
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-600"
                                    onClick={() => handleReject(payment.payment_id)}
                                    data-testid={`reject-payment-${payment.payment_id}`}
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Payment Status Tab */}
        <TabsContent value="status" className="mt-6">
          <PaymentStatusTab
            payments={payments}
            tenants={tenants}
            leases={leases}
            statusPeriod={statusPeriod}
            setStatusPeriod={setStatusPeriod}
          />
        </TabsContent>
      </Tabs>

      {/* Payment Details Dialog */}
      <Dialog open={!!selectedPayment} onOpenChange={() => setSelectedPayment(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Payment Details</DialogTitle>
          </DialogHeader>
          {selectedPayment && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
                <Avatar className="w-12 h-12">
                  <AvatarFallback className="bg-[#6B8E6B] text-white">
                    {getInitials(selectedPayment.tenant?.name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium">{selectedPayment.tenant?.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedPayment.tenant?.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Amount</p>
                  <p className="font-semibold text-lg">{formatCurrency(selectedPayment.amount)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Period</p>
                  <p className="font-medium">{selectedPayment.period_month || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Payment Type</p>
                  <p className="font-medium capitalize">{selectedPayment.payment_type}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Method</p>
                  <Badge className={getPaymentMethodColor(selectedPayment.payment_method)}>
                    {selectedPayment.payment_method?.toUpperCase()}
                  </Badge>
                </div>
                <div>
                  <p className="text-muted-foreground">Payment Date</p>
                  <p className="font-medium">{selectedPayment.payment_date ? formatDate(selectedPayment.payment_date) : '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Status</p>
                  <Badge className={getStatusColor(selectedPayment.status)}>{selectedPayment.status}</Badge>
                </div>
                {selectedPayment.reference_number && (
                  <div>
                    <p className="text-muted-foreground">Reference Number</p>
                    <p className="font-mono font-medium">{selectedPayment.reference_number}</p>
                  </div>
                )}
                <div>
                  <p className="text-muted-foreground">Submitted</p>
                  <p className="font-medium">{formatDate(selectedPayment.created_at)}</p>
                </div>
              </div>

              {selectedPayment.notes && (
                <div>
                  <p className="text-muted-foreground text-sm">Notes</p>
                  <p className="text-sm">{selectedPayment.notes}</p>
                </div>
              )}

              {selectedPayment.proof_of_payment && (
                <div>
                  <p className="text-muted-foreground text-sm mb-2">Proof of Payment</p>
                  <div className="border rounded-lg overflow-hidden">
                    <a href={getUploadUrl(selectedPayment.proof_of_payment)} target="_blank" rel="noopener noreferrer">
                      <img
                        src={getUploadUrl(selectedPayment.proof_of_payment)}
                        alt="Proof of payment"
                        className="w-full max-h-[400px] object-contain bg-muted cursor-pointer hover:opacity-90 transition-opacity"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const fallback = e.target.parentElement.querySelector('.proof-fallback');
                          if (fallback) fallback.style.display = 'flex';
                        }}
                        data-testid="proof-image"
                      />
                      <div className="proof-fallback hidden flex-col items-center justify-center py-8 text-gray-400">
                        <p>Unable to load image</p>
                        <p className="text-xs mt-1">Click to open in new tab</p>
                      </div>
                    </a>
                  </div>
                </div>
              )}

              {selectedPayment.receipt_number && (
                <div className="p-4 bg-emerald-50 dark:bg-emerald-950 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-emerald-600 dark:text-emerald-400">Receipt Number</p>
                      <p className="font-mono font-semibold text-emerald-800 dark:text-emerald-300">{selectedPayment.receipt_number}</p>
                    </div>
                    {selectedPayment.status === 'verified' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-emerald-700 border-emerald-300 hover:bg-emerald-100"
                        onClick={() => handleDownloadReceipt(selectedPayment.payment_id, selectedPayment.receipt_number)}
                        disabled={downloading === selectedPayment.payment_id}
                        data-testid="download-receipt-detail-btn"
                      >
                        {downloading === selectedPayment.payment_id ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        Download Receipt
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {(!isManager || can('verify_payments')) && (
              <Button
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => handleDeletePayment(selectedPayment)}
                data-testid="delete-payment-detail-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            )}
            {selectedPayment?.status === 'pending' && (!isManager || can('verify_payments')) && (
              <>
                <Button
                  variant="destructive"
                  onClick={() => handleReject(selectedPayment.payment_id)}
                  disabled={processing}
                >
                  Reject
                </Button>
                <Button
                  onClick={() => handleVerify(selectedPayment.payment_id)}
                  disabled={processing}
                  className="bg-emerald-500 hover:bg-emerald-600"
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Verify Payment
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Payment Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-[#C85A42]" />
              Edit Payment
            </DialogTitle>
            <DialogDescription>
              Update payment details
            </DialogDescription>
          </DialogHeader>

          {editingPayment && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-muted rounded-lg text-sm">
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-[#6B8E6B] text-white text-xs">
                    {getInitials(editingPayment.tenant?.name)}
                  </AvatarFallback>
                </Avatar>
                <span className="font-medium">{editingPayment.tenant?.name}</span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-pay-amount">{t('common.amount')}</Label>
                  <Input
                    id="edit-pay-amount"
                    type="number"
                    step="0.01"
                    value={editForm.amount}
                    onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                    data-testid="edit-payment-amount"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-pay-date">Payment Date</Label>
                  <Input
                    id="edit-pay-date"
                    type="date"
                    value={editForm.payment_date}
                    onChange={(e) => setEditForm({ ...editForm, payment_date: e.target.value })}
                    data-testid="edit-payment-date"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('payments.paymentType')}</Label>
                  <Select value={editForm.payment_type} onValueChange={(v) => setEditForm({ ...editForm, payment_type: v })}>
                    <SelectTrigger data-testid="edit-payment-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rent">{t('payments.rent')}</SelectItem>
                      <SelectItem value="deposit">{t('payments.deposit')}</SelectItem>
                      <SelectItem value="advance">{t('payments.advance')}</SelectItem>
                      <SelectItem value="utility">{t('payments.utility')}</SelectItem>
                      <SelectItem value="other">{t('payments.other')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('payments.paymentMethod')}</Label>
                  <Select value={editForm.payment_method} onValueChange={(v) => setEditForm({ ...editForm, payment_method: v })}>
                    <SelectTrigger data-testid="edit-payment-method">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">{t('payments.cash')}</SelectItem>
                      <SelectItem value="gcash">{t('payments.gcash')}</SelectItem>
                      <SelectItem value="maya">{t('payments.maya')}</SelectItem>
                      <SelectItem value="bank">{t('payments.bank')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('payments.periodMonth')}</Label>
                  <Input
                    type="month"
                    value={editForm.period_month}
                    onChange={(e) => setEditForm({ ...editForm, period_month: e.target.value })}
                    data-testid="edit-payment-period"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Reference #</Label>
                  <Input
                    value={editForm.reference_number}
                    onChange={(e) => setEditForm({ ...editForm, reference_number: e.target.value })}
                    placeholder="e.g. GCash ref #"
                    data-testid="edit-payment-reference"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('common.notes')}</Label>
                <Textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={2}
                  data-testid="edit-payment-notes"
                />
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={handleSaveEdit}
                  disabled={savingEdit}
                  className="bg-[#C85A42] hover:bg-[#B54A35]"
                  data-testid="save-edit-payment-btn"
                >
                  {savingEdit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  {t('common.save')}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
